import { useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import { checkIfImage, type CoreFileItem, CoreFilePreview, CoreIconButton, CoreSpinner, CoreXIcon } from "@verity/components/core";
import { PdfViewer } from "@verity/components/pdfviewer";
import type { ReferenceMaterialMode } from "@verity/database/prisma";
import i18n from "../../../../../i18n";
import FullScreenIcon from "../../../../../images/portal/fullscreen-square.svg?react";
import NoReferenceMaterialImage from "../../../../../images/reports/noReferenceMaterialImage.svg?react";
import styles from "./ReferenceMaterialPanel.module.css";

type ReferenceMaterialPanelProps = {
	loading: boolean;
	files: CoreFileItem[];
	mode: ReferenceMaterialMode | undefined;
};

function EmptyState() {
	return (
		<div className={styles.errorContainer}>
			<NoReferenceMaterialImage className={styles.errorImage} aria-hidden="true" />
			<p className={styles.errorTitle}>
				{i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.referenceMaterial.unavailableTitle")}
			</p>
			<p className={styles.errorDescription}>
				{i18n.t(
					"result.components.studentSelection.videoPlayer.videoPlayerTSX.referenceMaterial.unavailableDescription",
				)}
			</p>
		</div>
	);
}

export default function ReferenceMaterialPanel({ loading, files }: ReferenceMaterialPanelProps) {
	const [fullScreenShowing, setFullScreenShowing] = useState(false);
	const file = files[0];

	if (loading) {
		return (
			<div className={styles.container}>
				<div className={styles.imageContainer}>
					<CoreSpinner size={100} />
				</div>
			</div>
		);
	}

	if (!file) {
		return (
			<EmptyState />
		);
	}

	if (!checkIfImage(file.name)) {
		return (
			<div className={styles.container} style={{backgroundColor: "unset", overflow: "unset", borderRadius: "unset"}}>
				<div className={styles.pdfViewerHost}>
					<PdfViewer file={file} />
				</div>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<div className={styles.imageContainer}>
				<CoreFilePreview
					file={file}
					slotProps={{
						container: { className: styles.imagePreviewContainer },
						imageWrapper: { className: styles.imagePreviewWrapper },
						image: { className: styles.image },
					}}
					renderOverlay={() => (
						<CoreIconButton
							variant="plain"
							className={styles.fullScreenButton}
							onClick={() => setFullScreenShowing(true)}
							aria-haspopup="dialog"
							aria-label={
								i18n.t(
									"result.components.studentSelection.videoPlayer.videoPlayerTSX.referenceMaterial.fullScreenAriaLabel",
								) as string
							}
						>
							<FullScreenIcon aria-hidden="true" />
						</CoreIconButton>
					)}
				/>
			</div>
			{fullScreenShowing && (
				<Dialog
					open
					fullScreen
					onClose={() => setFullScreenShowing(false)}
					PaperProps={{ sx: { backgroundColor: "#212121E5", padding: 0 }, "aria-modal": "true" }}
				>
					<DialogContent className={styles.fullScreenDialogContent}>
						<div className={styles.fullScreenTopRow}>
							<CoreIconButton
								variant="plain"
								color="dark"
								className={styles.fsButton}
								onClick={() => setFullScreenShowing(false)}
								aria-label={
									i18n.t(
										"result.components.studentSelection.videoPlayer.videoPlayerTSX.referenceMaterial.closeFullScreenAriaLabel",
									) as string
								}
							>
								<CoreXIcon ariaHidden="true" />
							</CoreIconButton>
						</div>
						<div className={styles.fullScreenImageContainer}>
							<img src={file.url} alt={file.name} className={styles.fullScreenImage} />
						</div>
					</DialogContent>
				</Dialog>
			)}
		</div>
	);
}
